import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";
import Cookies from "universal-cookie";
import { useTranslation } from "react-i18next";

import { accountService } from "@/_services";
import { DrawerFooter, NavToolbar } from "@/_components";
import { history } from "@/_helpers";

import { useTheme } from "@mui/material/styles";

import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";

// Nav Imports
import AppBar from "@mui/material/AppBar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Drawer from "@mui/material/Drawer";
import Hidden from "@mui/material/Hidden";
import Grid from "@mui/material/Grid";

// Icon Imports
import Dashboard from "@mui/icons-material/Dashboard";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import CreditCard from "@mui/icons-material/CreditCard";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PermDeviceInformation from "@mui/icons-material/PermDeviceInformation";
import Help from "@mui/icons-material/Help";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";

const StyledListItem = withStyles(() => ({
  root: {
    color: "#6b7c96",
    fontSize: 16,
    fontWeight: 600,
    minHeight: 45,
    margin: 0,
    "&:before, &:after": {
      top: 1,
      content: '" "',
      height: 43,
      position: "absolute",
    },
    "&:before": {
      left: 0,
      width: 8,
    },
    "&:after": {
      left: 0,
      height: 43,
    },
    "&:hover, &:focus": {
      background: "initial",
      color: "#1c355e",
      "&:before": {
        background: "#6b7c96",
      },
    },
  },
  selected: {
    "&.Mui-selected": {
      color: "#1c355e",
      background: "initial",
      "&:hover, &:focus": {
        background: "initial",
      },
      "&:after": {
        width: 8,
        background: "#76bc21",
      },
    },
  },
}))(ListItem);
const drawerWidth = 240;
const useStyles = makeStyles((theme) => ({
  drawer: {
    [theme.breakpoints.up("md")]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  appBar: {
    [theme.breakpoints.up("md")]: {
      width: `calc(100%-${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
  menuButton: {
    [theme.breakpoints.up("md")]: { display: "none" },
    [theme.breakpoints.up("sm")]: {
      marginRight: theme.spacing(2),
    },
    [theme.breakpoints.down("sm")]: {
      marginRight: theme.spacing(1),
    },
  },
  // Necessary for content to be below app bar
  toolbar: theme.mixins.toolbar,
  drawerPaper: { width: drawerWidth },
  badgeColor: { backgroundColor: "#d1495b" },
  logo: {
    [theme.breakpoints.up("sm")]: {
      width: 168,
      height: 34,
    },
    [theme.breakpoints.down("sm")]: {
      width: 138,
      height: 28,
    },
  },
  drawerFooter: {
    flexGrow: 1,
    textAlign: "center",
  },
}));

function Nav({ unreadNotifications, selectedNav, updateSelected, ...props }) {
  const countryCode = sessionStorage.getItem("LSCountryCode");
  const classes = useStyles();
  const theme = useTheme();
  const { window } = props;
  const cookies = new Cookies();
  const user = cookies.get("lifesaferUser");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();

  const updateSelectedNav = (index) => {
    updateSelected(index);
  };

  // only show nav when logged in
  if (
    user === undefined ||
    user.jwtToken === undefined ||
    user.jwtToken === null ||
    user.jwtToken.length < 20
  )
    return null;

  const ListItemsNew = (
    <>
      <StyledListItem
        button
        component={RouterLink}
        to="/"
        selected={selectedNav === "/"}
        onClick={() => {
          setMobileOpen(false);
          updateSelectedNav("/");
        }}
      >
        <ListItemIcon style={{ color: "#1c355e" }}>
          <Dashboard />
        </ListItemIcon>
        {t("navigation.dashboard")}
      </StyledListItem>
      <StyledListItem
        button
        component={RouterLink}
        to="/appointments"
        selected={
          selectedNav === "/appointments" ||
          selectedNav === "/schedule" ||
          selectedNav === "/cancel"
        }
        onClick={() => {
          setMobileOpen(false);
          updateSelectedNav("/appointments");
        }}
      >
        <ListItemIcon style={{ color: "#1c355e" }}>
          <InsertInvitationIcon />
        </ListItemIcon>
        {t("navigation.appointments")}
      </StyledListItem>
      {countryCode === "USA" && (
        <StyledListItem
          button
          component={RouterLink}
          to="/payments"
          selected={selectedNav === "/payments"}
          onClick={() => {
            setMobileOpen(false);
            updateSelectedNav("/payments");
          }}
        >
          <ListItemIcon style={{ color: "#1c355e" }}>
            <CreditCard />
          </ListItemIcon>
          {t("navigation.payments")}
        </StyledListItem>
      )}
      <StyledListItem
        button
        component={RouterLink}
        to="/documents"
        selected={selectedNav === "/documents"}
        onClick={() => {
          setMobileOpen(false);
          updateSelectedNav("/documents");
        }}
      >
        <ListItemIcon style={{ color: "#1c355e" }}>
          <AssignmentOutlinedIcon />
        </ListItemIcon>
        {t("navigation.documents")}
      </StyledListItem>
      <StyledListItem
        button
        component={RouterLink}
        to="/resources"
        selected={selectedNav === "/resources"}
        onClick={() => {
          setMobileOpen(false);
          updateSelectedNav("/resources");
        }}
      >
        <ListItemIcon style={{ color: "#1c355e" }}>
          <PermDeviceInformation />
        </ListItemIcon>
        {t("navigation.resources")}
      </StyledListItem>
      <StyledListItem
        button
        component={RouterLink}
        to="/help"
        selected={selectedNav === "/help"}
        onClick={() => {
          setMobileOpen(false);
          updateSelectedNav("/help");
        }}
      >
        <ListItemIcon style={{ color: "#1c355e" }}>
          <Help />
        </ListItemIcon>
        {t("navigation.help")}
      </StyledListItem>
      <StyledListItem
        button
        component={RouterLink}
        to="/login"
        selected={selectedNav === "/logOut"}
        onClick={accountService.logout}
      >
        <ListItemIcon style={{ color: "#1c355e" }}>
          <ExitToAppOutlinedIcon />
        </ListItemIcon>
        {t("navigation.logOut")}
      </StyledListItem>
    </>
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const drawer = (
    <Grid container direction="column" style={{ flexGrow: 1, minHeight: 420 }}>
      <Grid item className={classes.toolbar} style={{ textAlign: "center" }}>
        <img
          className={classes.logo}
          src={"./src/_images/LogoLimited.png"}
          alt="LifeSafer"
          style={{ marginTop: 15 }}
          onClick={() => {
            setMobileOpen(false);
            history.push("/");
          }}
        />
      </Grid>
      <Grid item>
        <List>{ListItemsNew}</List>
      </Grid>
      <Grid item className={classes.drawerFooter}>
        <Grid
          container
          direction="column"
          justifyContent="flex-end"
          style={{ height: "100%" }}
        >
          <Grid item>
            <DrawerFooter />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
  const container =
    window !== undefined ? () => window().document.body : undefined;

  const invisible = true;

  return (
    <div>
      <AppBar
        position="fixed"
        className={classes.appBar}
        style={{ backgroundColor: "white" }}
        elevation={2}
      >
        <NavToolbar
          selectedNav={selectedNav}
          unreadNotifications={unreadNotifications}
          invisible={invisible}
          handleDrawerToggle={handleDrawerToggle}
          setMobileOpen={setMobileOpen}
          updateSelectedNav={updateSelectedNav}
        />
      </AppBar>
      <nav className={classes.drawer} aria-label="drawer pages">
        <Hidden smUp implementation="css">
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{ paper: classes.drawerPaper }}
            ModalProps={{
              keepMounted: true, // Better performance on mobile devices
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden mdDown implementation="css">
          <Drawer
            classes={{ paper: classes.drawerPaper }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>
    </div>
  );
}

Nav.propTypes = {
  unreadNotifications: PropTypes.number.isRequired,
  selectedNav: PropTypes.string,
  updateSelected: PropTypes.func.isRequired,
  window: PropTypes.object,
  documentNavVisible: PropTypes.bool,
};

export { Nav };
